import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Switch, List, ListItem, ListItemText, ListItemAvatar, CircularProgress,
} from '@mui/material'
import Avatar from 'components/Avatar'
import { listPortfolioUsers } from 'services/portfolioService'
import { getPortfolioFolderUsers } from 'services/portfolioDocumentsService'
import { hideEmail, isStaffUser } from 'utils/user'
import { togglePortfolioFolderAccessRoutine } from 'ducks/portfolioDocuments/actions'
import { toast } from 'react-toastify'

export default function ManagePortfolioFolderPermissions({ portfolioId, folder, onClose }) {
  const dispatch = useDispatch()
  const [members, setMembers] = useState([])
  const [granted, setGranted] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all([listPortfolioUsers(portfolioId), getPortfolioFolderUsers(portfolioId, folder.id)])
      .then(([membersRes, usersRes]) => {
        if (!active) return
        const portfolioMembers = (membersRes.data.data || [])
          .filter((m) => m.user && !isStaffUser(m.user))
          .map((m) => ({
            ...m.user,
            name: [m.user.firstName, m.user.lastName].filter(Boolean).join(' '),
          }))
        setMembers(portfolioMembers)
        const current = {}
        ;(usersRes.data.data || []).forEach((id) => { current[id] = true })
        setGranted(current)
      })
      .catch(() => toast.error('Failed to load portfolio members'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [portfolioId, folder.id])

  const toggle = (userId, value) => {
    setGranted((g) => ({ ...g, [userId]: value }))
    dispatch(togglePortfolioFolderAccessRoutine.trigger({
      portfolioId,
      userId,
      folders: [{ id: folder.id, permission: value }],
    }))
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage access: {folder.name}</DialogTitle>
      <DialogContent dividers>
        <p style={{ color: '#666', marginTop: 0 }}>
          By default every portfolio member can see this folder. Grant access below to restrict
          it to the chosen members (the grant cascades to sub-folders).
        </p>
        {loading && <CircularProgress size={20} />}
        {!loading && members.length === 0 && (
          <p style={{ color: '#888' }}>No individual members have been shared this portfolio yet.</p>
        )}
        {!loading && members.length > 0 && (
          <List>
            {members.map((m) => (
              <ListItem
                key={m.id}
                secondaryAction={
                  <Switch
                    checked={!!granted[m.id]}
                    onChange={(e) => toggle(m.id, e.target.checked)}
                  />
                }
              >
                <ListItemAvatar>
                  <Avatar firstName={m.name} email={m.email} src={m.avatar} size={36} />
                </ListItemAvatar>
                <ListItemText
                  primary={m.name || hideEmail(m.email)}
                  secondary={m.name ? hideEmail(m.email) : null}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
